/**
 * The one way the web app talks to the API: same origin, the session cookie, the error shape
 * (`code`, `message`, `details`) turned into `ApiRequestError`, and the CSRF token on every write.
 * The token lives only in memory (it comes with `/api/v1/me`); nothing is written to browser storage.
 * The registry's generated client (b1-registry) builds its action hooks on `callAction`.
 */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** The token for this session, for clients that make their own requests (the action client). */
export function getCsrfToken(): string | null {
  return csrfToken;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export async function apiFetch<T>(
  path: string,
  init: { method?: Method; body?: unknown; signal?: AbortSignal | undefined } = {},
): Promise<T> {
  const method = init.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };
  if (init.body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["x-adea-csrf"] = csrfToken;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: init.body === undefined ? null : JSON.stringify(init.body),
      credentials: "same-origin",
      signal: init.signal ?? null,
    });
  } catch (error) {
    if (init.signal?.aborted) throw error;
    throw new ApiRequestError(0, "offline", "offline");
  }

  const body: unknown = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const shape = (body ?? {}) as { code?: string; message?: string; details?: unknown };
    throw new ApiRequestError(
      response.status,
      shape.code ?? "internal",
      shape.message ?? response.statusText,
      shape.details,
    );
  }
  return body as T;
}

/** Calls an action through the registry's one gate. */
export function callAction<TOutput>(name: string, input: unknown, signal?: AbortSignal): Promise<TOutput> {
  return apiFetch<TOutput>(`/api/v1/actions/${encodeURIComponent(name)}`, {
    method: "POST",
    body: input,
    signal,
  });
}
